import React, { useContext, useState } from 'react';
import { View, Pressable, Switch } from 'react-native';
import { useMutation } from '@apollo/client';

import ClientContext from 'app/src/contexts/ClientContext';
import mobileTable from 'app/src/elements/Table/MobileTable';
import RelayTable from 'app/src/elements/Table/RelayTable';
import { Text, Title, Setting, SettingLabel } from 'app/src/styles';
import colors from 'app/src/styles/colors';
import { ActionText, CellText } from 'app/src/styles/table';
import { datetimeShort } from 'app/src/utils/formatting';

import MobileRow from './MobileRow';
import { SYSTEM_ALERTS, DISMISS_SYSTEM_ALERT_NOTICES } from './queries';
import SystemAlert from './show';

const columns = [
  {
    key: 'message',
    label: 'Message',
    content: ({ value }) => (
      <CellText>{value}</CellText>
    ),
  },
  {
    key: 'createdAt',
    label: 'Time',
    sort: 'desc',
    content: datetimeShort,
  },
  {
    key: 'status',
    label: 'Status',
    sort: 'prevent',
    content: ({ entry }) => (
      <View style={{ flexDirection: 'row', gap: 4, alignItems: 'center' }}>
        {entry.alert && (
          <Text style={{ color: '#ff0000', fontSize: 24 }}>⚠</Text>
        )}
        {entry.dismissedAt && (
          <Text>✅</Text>
        )}
      </View>
    )
  },
  {
    key: 'actions',
    label: 'Actions',
    sort: 'prevent',
  },
];

const SystemAlerts = () => {
  const { isMobile } = useContext(ClientContext);
  const [showDetail, setShowDetail] = useState();
  const [scopes, setScopes] = useState(['undismissed']);
  const [refetch, setRefetch] = useState();
  const [lastLoadedAt, setLastLoadedAt] = useState();
  const [noticesCount, setNoticesCount] = useState(0);

  const [dismissSystemAlertNotices] = useMutation(
    DISMISS_SYSTEM_ALERT_NOTICES,
    { onCompleted: () => refetch() }
  );

  const confirmDismissSystemAlertNotices = () => {
    if (window.confirm('Dismiss all non-alert notices?')) {
      dismissSystemAlertNotices({ variables: { maxCreatedAt: lastLoadedAt } });
    }
  };

  const queryVariables = { scopes };

  columns[columns.length - 1].content = ({ entry }) => (
    <Pressable onPress={() => setShowDetail(entry)}>
      <ActionText>View Details</ActionText>
    </Pressable>
  );

  const handleDataUpdated = data => {
    const notices = new Array();

    if (data) {
      data.systemAlerts.edges.forEach(systemAlert => {
        if (!systemAlert.node.alert && !systemAlert.node.dismissedAt) {
          notices.push(systemAlert.node);
        }
      });
    }

    setLastLoadedAt(notices[0]?.__loadedAt);
    setNoticesCount(notices.length);
  };

  return (
    <View>
      <Title>System Notices and Alerts</Title>

      <Setting>
        <SettingLabel>Include dismissed?</SettingLabel>
        <Switch
          value={!scopes.includes('undismissed')}
          onValueChange={value => setScopes(value ? [] : ['undismissed'])}
          activeTrackColor={colors.lightButton}
          activeThumbColor={colors.button}
        />
      </Setting>

      {noticesCount > 0 && (
        <View style={{ flexDirection: 'row', gap: 16 }}>
          <Pressable onPress={confirmDismissSystemAlertNotices}>
            <Text style={{ fontWeight: 600 }}>
              Dismiss All Notices
            </Text>
          </Pressable>

          {!isMobile && (
            <Text>
              Note: Dismissing a notice dismissed it system-wide.
            </Text>
          )}
        </View>
      )}

      <RelayTable
        query={SYSTEM_ALERTS}
        queryName="systemAlerts"
        queryVariables={queryVariables}
        columns={columns}
        dataUpdated={handleDataUpdated}
        setRefetch={setRefetch}
        component={isMobile ? mobileTable : null}
        componentProps={{ rowComponent: MobileRow }}
      />

      {showDetail && (
        <SystemAlert systemAlert={showDetail} setShow={setShowDetail} />
      )}
    </View>
  );
};

export default SystemAlerts;
